import { useMutation } from "@apollo/client";
import { useWS, useAuth } from "@hooks";
import axios from "axios";
import mixpanel from "mixpanel-browser";
import React, { useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useSpring, animated, config } from "react-spring";

import ConfirmAbandonEditsModal from "./modals/ConfirmAbandonEditsModal";
import ConfirmTemplateConstructionModal from "./modals/ConfirmTemplateConstructionModal";
import ConfirmTemplateTypeModal from "./modals/ConfirmTemplateTypeModal";
import ResetTemplateModal from "./modals/ResetTemplateModal";
import {
  cleanExtractedData,
  processSoapFields,
  processCustomFields,
} from "./utils";
import { signS3Attachment } from "../../../api/SignS3";
import {
  UPLOAD_PDF_AND_EXTRACT_TEXT,
  CREATE_TEMPLATE_PREVIEW,
} from "../../../graphql/mutations/AutoTemplates";
import { alert } from "../../common/Alert";
import {
  SOAP_FIELDS,
  CUSTOM_FIELDS,
  TEMPLATE_TYPES,
  TEMPLATE_FIELDS,
  TRANSCRIPT_SAMPLE_MAPPINGS,
} from "../constants";
import FooterSection from "./components/FooterSection";
import FormBuilderSection from "./components/FormBuilderSection";
import MetadataSection from "./components/MetadataSection";
import PreviewSection from "./components/PreviewSection";
import ConfirmTemplateDeleteModal from "./modals/ConfirmTemplateDeleteModal";

const TemplateBuilder = ({
  control,
  title,
  isNew = false,
  handleDelete,
  shouldShowDeleteModal,
  setShouldShowDeleteModal,
  refetch,
  templateConstructionChoice,
  setTemplateConstructionChoice,
}) => {
  const { getValues, setValue } = useFormContext(control);

  // Navigation and URL Parameters Hooks
  const navigate = useNavigate();
  const { uuid: templateId } = useParams();

  // Current User Data
  const { userUuid } = useAuth();

  // Websockets
  const wsRef = useRef(null);
  const { connectWS } = useWS();

  // State Declarations
  const [loadingExtraction, setLoadingExtraction] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [
    showConfirmTemplateTypeModal,
    setShowConfirmTemplateTypeModal,
  ] = useState(false);
  const [
    showConfirmTemplateConstructionModal,
    setShowConfirmTemplateConstructionModal,
  ] = useState(false);
  const [
    showResetTemplateConstructionModal,
    setShowResetTemplateConstructionModal,
  ] = useState(false);
  const [
    showConfirmAbandonEditsModal,
    setShowConfirmAbandonEditsModal,
  ] = useState(false);

  const [highlightFileUploader, setHighlightFileUploader] =
    useState(false);
  const [shouldExpandPreview, setShouldExpandPreview] =
    useState(false);

  // Mutations
  const [uploadPdfAndExtractText] = useMutation(
    UPLOAD_PDF_AND_EXTRACT_TEXT,
  );
  const [createTemplatePreview] = useMutation(
    CREATE_TEMPLATE_PREVIEW,
  );

  // Effects
  const styles = useSpring({
    formWidth: shouldExpandPreview ? "40%" : "100%",
    previewWidth: shouldExpandPreview ? "60%" : "40%",
    formOpacity: shouldExpandPreview ? "50%" : "100%",
    config: config.gentle,
  });

  // Handlers
  const handleTemplatePreviewUpdate = async (e) => {
    let message = JSON.parse(e.data);
    if (message?.data?.template_uuid != templateId) {
      return;
    }
    if (message?.event === "template_preview_complete") {
      try {
        const { data } = await refetch();
        if (data?.autoTemplateById) {
          const template = data.autoTemplateById;

          setValue(
            "preview",
            template.templatePreviews[0].previewContent,
            { shouldDirty: false },
          );
        }
        alert(
          "success",
          `Your preview has been successfully generated. Check it out! If you're happy with it, click "Save Template".`,
        );
        mixpanel.track("Template preview complete", {
          templateUuid: message?.data?.template_uuid,
          templateName: message?.data?.template_name,
        });
      } catch {
        alert(
          "error",
          "There was an error updating the template preview. Please refresh your page.",
        );
      } finally {
        setLoadingPreview(false);
      }
    } else if (message?.event === "template_preview_failed") {
      mixpanel.track("Template preview failed", {
        templateUuid: message?.data?.template_uuid,
        templateName: message?.data?.template_name,
      });
    }
    wsRef.current.removeEventListener(
      "message",
      handleTemplatePreviewUpdate,
    );
  };

  const handlePreview = async () => {
    setLoadingPreview(true);
    setShouldExpandPreview(true);

    const {
      templateFields,
      templateType,
      userPreferences,
      sampleTranscript,
    } = getValues();

    const createTemplatePreviewInput = {
      templateFields,
      templateType,
      userPreferences,
      templateUuid: templateId,
      sampleTranscript,
    };

    try {
      await createTemplatePreview({
        variables: { createTemplatePreviewInput },
      });

      wsRef.current = connectWS(`user/${userUuid}`);

      // WebSocket timeout
      let wsTimeout = setTimeout(() => {
        alert(
          "warning",
          "It has been two minutes without a successful preview generation. Please try again.",
        );
        setLoadingPreview(false);
      }, 120000); // 2-minute timeout

      // Scroll to top of page
      window.scrollTo({ top: 0, behavior: "smooth" });

      // Reset timeout when a message is received
      wsRef.current.addEventListener("message", (e) => {
        clearTimeout(wsTimeout);
        handleTemplatePreviewUpdate(e);
      });
    } catch (err) {
      if (wsRef.current) {
        wsRef.current.removeEventListener(
          "message",
          handleTemplatePreviewUpdate,
        );
      }
      alert("error", "There was an error generating the preview.");
      setLoadingPreview(false);
    }
  };

  async function uploadPdf(file) {
    if (!file) {
      throw new Error("No file provided for upload.");
    }

    //hardcoding PDF as that's the only file type we accept
    const fileType = "pdf";

    // Get signed S3 URL
    const signS3Response = await signS3Attachment(
      fileType,
      "templates",
      "application/pdf",
    );
    const { data, storageObjectName } = signS3Response.data;
    const { fields } = data;

    const fileData = new FormData();
    for (const key in fields) {
      fileData.append(key, fields[key]);
    }
    fileData.append("file", file);
    fileData.append("Content-Type", file.type);

    await axios.post(data.url, fileData, {
      headers: { "Access-Control-Allow-Origin": true },
    });
    return {
      fileName: file.name,
      storageObjectName,
      contentType: file.type,
    };
  }

  // File Upload Handlers
  const handleFileUpload = async (file) => {
    setLoadingExtraction(true);
    try {
      const { storageObjectName } = await uploadPdf(file);
      const formData = getValues();
      const response = await uploadPdfAndExtractText({
        variables: {
          storageObjectName: storageObjectName,
          templateType: formData?.templateType,
          templateUuid: templateId,
        },
      });
      const data = response.data?.uploadPdfAndExtractText;

      if (data) {
        const cleanedData = cleanExtractedData(data);
        const updatedFields =
          formData.templateType === TEMPLATE_TYPES.SOAP
            ? processSoapFields(cleanedData)
            : processCustomFields(
                cleanedData[TEMPLATE_FIELDS.CUSTOM],
              );

        setValue("templateFields", updatedFields, {
          shouldDirty: true,
        });
      }
    } catch (err) {
      mixpanel.track("Error extracting PDF text", {
        Location: "Template Builder",
        Message: err.message,
      });
      alert(
        "error",
        "There was an error extracting the text from your PDF. Try again or use a different PDF.",
      );
    } finally {
      setLoadingExtraction(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file?.type === "application/pdf") {
      handleFileUpload(file);
    } else {
      alert("warning", "Please upload a valid PDF file.");
    }
  };

  // Template Type Change Handler
  const handleTemplateTypeChange = (e) => {
    setValue("templateType", e.target.value);
    const modalOpened = openConfirmationOpenIfFieldsFilled(
      setShowConfirmTemplateTypeModal,
    );
    if (!modalOpened) {
      refreshTemplateFields(e.target.value);
    }
  };

  // Utility Handler: Opens confirmation modal if any fields have values
  const openConfirmationOpenIfFieldsFilled = (setShowModal) => {
    const formData = getValues();
    if (formData.templateFields.some((field) => field.fieldValue)) {
      setShowModal(true);
      return true;
    }
  };

  // Utility Handler: Refreshes the template fields based on the new template type
  const refreshTemplateFields = (newTemplateType) => {
    setValue("templateType", newTemplateType);
    setValue(
      "templateFields",
      newTemplateType === TEMPLATE_TYPES.SOAP
        ? SOAP_FIELDS
        : CUSTOM_FIELDS,
    );
    setValue("userPreferences", "");
  };

  // Modal Confirmations / Handlers for Changing Template Type and Construction
  const confirmChangeTemplateType = () => {
    setShowConfirmTemplateTypeModal(false);

    const templateType = getValues("templateType");

    refreshTemplateFields(templateType);
  };

  const confirmChangeTemplateConstruction = () => {
    const newConstructionChoice =
      templateConstructionChoice === "upload" ? "scratch" : "upload";
    setTemplateConstructionChoice(newConstructionChoice);
    setShowConfirmTemplateConstructionModal(false);
    const templateType = getValues("templateType");
    refreshTemplateFields(templateType);
  };

  const cancelChangeTemplateType = () => {
    setShowConfirmTemplateTypeModal(false);
  };

  const handleClearInspirationChips = () => {
    setValue(
      "templateFields",
      getValues("templateFields").map((field) => ({
        ...field,
        fieldValue: "",
      })),
      {
        shouldDirty: true,
      },
    );
    setValue("userPreferences", "");
    setValue("templateVariant", "DEFAULT");
    setValue(
      "sampleTranscript",
      TRANSCRIPT_SAMPLE_MAPPINGS.DEFAULT.sampleTranscript,
    );
  };

  const handleClearSampleTranscript = () => {
    setValue(
      "sampleTranscript",
      TRANSCRIPT_SAMPLE_MAPPINGS.DEFAULT.sampleTranscript,
      {
        shouldDirty: true,
      },
    );
  };

  const handleConfirmResetTemplateConstruction = () => {
    const templateType = getValues("templateType");
    refreshTemplateFields(templateType);
    setTemplateConstructionChoice("upload");
    setShowResetTemplateConstructionModal(false);
    handleClearInspirationChips();
    handleClearSampleTranscript();
  };

  // Handler for highlighting the file uploader
  const handleHighlightClick = () => {
    setHighlightFileUploader(true);
    setTimeout(() => setHighlightFileUploader(false), 500);
  };

  const handleConfirmAbandonEdits = () => {
    if (isNew) {
      handleDelete(true);
    } else {
      navigate("/templates");
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 flex flex-col">
      {/* Modals */}
      <ConfirmTemplateTypeModal
        shouldShow={showConfirmTemplateTypeModal}
        hideModal={cancelChangeTemplateType}
        confirmAction={confirmChangeTemplateType}
      />
      <ConfirmTemplateConstructionModal
        shouldShow={showConfirmTemplateConstructionModal}
        hideModal={() =>
          setShowConfirmTemplateConstructionModal(false)
        }
        confirmAction={confirmChangeTemplateConstruction}
      />
      <ResetTemplateModal
        shouldShow={showResetTemplateConstructionModal}
        hideModal={() => setShowResetTemplateConstructionModal(false)}
        confirmAction={handleConfirmResetTemplateConstruction}
      />
      <ConfirmAbandonEditsModal
        shouldShow={showConfirmAbandonEditsModal}
        hideModal={() => setShowConfirmAbandonEditsModal(false)}
        confirmAction={handleConfirmAbandonEdits}
      />
      <ConfirmTemplateDeleteModal
        shouldShow={shouldShowDeleteModal}
        hideModal={() => setShouldShowDeleteModal(false)}
        confirmAction={() => handleDelete(true)}
      />

      <h2 className="text-3xl font-semibold text-gray-900 dark:text-gray-200 mt-4">
        {title}
      </h2>

      <div className="flex flex-row w-full">
        <div className="w-full">
          {/* Two-column layout: Form Builder & Preview */}
          <div className="flex lg:flex-row flex-col mt-4">
            <animated.div
              style={{
                width: styles.formWidth,
                opacity: styles.formOpacity,
              }}
              className="flex flex-col lg:pr-6 space-y-8"
              onClick={() => {
                if (shouldExpandPreview && !loadingPreview) {
                  setShouldExpandPreview(false);
                }
              }}
            >
              <MetadataSection
                control={control}
                handleTemplateTypeChange={handleTemplateTypeChange}
              />
              <FormBuilderSection
                control={control}
                handleTemplateTypeChange={handleTemplateTypeChange}
                setShowResetTemplateConstructionModal={
                  setShowResetTemplateConstructionModal
                }
                setShowConfirmTemplateConstructionModal={
                  setShowConfirmTemplateConstructionModal
                }
                openConfirmationOpenIfFieldsFilled={
                  openConfirmationOpenIfFieldsFilled
                }
                templateConstructionChoice={
                  templateConstructionChoice
                }
                setTemplateConstructionChoice={
                  setTemplateConstructionChoice
                }
                handleHighlightClick={handleHighlightClick}
                handleFileChange={handleFileChange}
                highlightFileUploader={highlightFileUploader}
                loadingExtraction={loadingExtraction}
                handleClearInspirationChips={
                  handleClearInspirationChips
                }
              />
            </animated.div>
            <animated.div
              style={{
                width: styles.previewWidth,
                opacity: styles.previewOpacity,
              }}
              className="overflow-y-auto border-l border-gray-100 dark:border-gray-700 pl-6"
            >
              <PreviewSection
                control={control}
                loadingPreview={loadingPreview}
                handleClearSampleTranscript={
                  handleClearInspirationChips
                }
              />
            </animated.div>
          </div>

          {/* Footer Buttons */}
          <FooterSection
            control={control}
            setShouldShowDeleteModal={setShouldShowDeleteModal}
            loadingExtraction={loadingExtraction}
            handlePreview={handlePreview}
            loadingPreview={loadingPreview}
            shouldExpandPreview={shouldExpandPreview}
            setShouldExpandPreview={setShouldExpandPreview}
          />
        </div>
      </div>
    </div>
  );
};

export default TemplateBuilder;
